import React, { ReactElement } from "react";
import { NavLink } from "react-router";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import { Box, Typography } from "@mui/material";
import { useHasPermission } from "@/hooks";

interface ProfileMenuItemProps {
  link?: string;
  icon: React.ReactNode;
  codes: string[];
  label: string;
  desc?: string;
  onClick?: () => void;
}

/**
 * ProfileMenuItem component renders a menu item for the profile menu.
 * It displays an icon, label, and optional description.
 * The item is only shown if the user has permission for the specified codes.
 */
export function ProfileMenuItem({
  link = "",
  icon,
  codes,
  label,
  desc = "",
  onClick,
}: ProfileMenuItemProps): ReactElement {
  const { allow } = useHasPermission(codes, "common");

  const handleClick = (): void => {
    if (typeof onClick === "function") {
      onClick();
    }
  };

  if (!allow) {
    return <></>;
  }

  const itemContent = (
    <MenuItem
      onClick={!link ? handleClick : undefined}
      sx={{
        color: "primary.100",
      }}
    >
      <ListItemIcon
        sx={{
          color: "inherit",
        }}
      >
        {icon}
      </ListItemIcon>
      <Box>
        <Typography
          variant="body1"
          sx={{
            fontSize: 16,
            fontWeight: "light",
            textDecoration: "none",
            color: "primary.100",
          }}
        >
          {label}
        </Typography>
        {desc ? (
          <Typography variant="caption" sx={{ color: "grey" }}>
            {desc}
          </Typography>
        ) : null}
      </Box>
    </MenuItem>
  );

  return link ? (
    <NavLink to={link} onClick={handleClick}>
      {itemContent}
    </NavLink>
  ) : (
    itemContent
  );
}
